import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Utils
import { Table, kaReducer } from 'ka-table';
import { SortDirection, SortingMode } from 'ka-table/enums';
import { updateData, search } from 'ka-table/actionCreators';
import { useTranslation } from 'react-i18next';

// Icons
import { ReactComponent as ChevronUp } from '../../../assets/icons/chevron_up.svg';
import { ReactComponent as ChevronDown } from '../../../assets/icons/chevron_down.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';

// Components
import InputField from '../../atoms/InputField/InputField';

import styles from './GenericTable.module.scss';

function GenericTable({
  columns, rows, isSearchable, searchPlaceholder,
}) {
  const { t } = useTranslation();

  const [searchValue, setSearchValue] = useState('');

  const [tableProps, changeTableProps] = useState({
    columns,
    data: rows,
    rowKeyField: 'id',
    sortingMode: SortingMode.Single,
  });

  const dispatch = (action) => {
    changeTableProps((prevState) => kaReducer(prevState, action));
  };

  useEffect(() => {
    dispatch(updateData(rows));
  }, [rows]);

  useEffect(() => {
    dispatch(search(searchValue));
  }, [searchValue]);

  const getCell = (rowData, field, bold) => {
    if (bold) return <strong className="c-neutral-01 fw-600">{rowData[field]}</strong>;
    return rowData[field];
  };

  const getSortIcon = (column) => {
    const up = <ChevronUp width={15} height={15} />;
    const down = <ChevronDown width={15} height={15} />;
    return column.sortDirection === SortDirection.Ascend ? up : down;
  };

  return (
    <div>
      {isSearchable ? (
        <div className="d-flex m-b-20">
          <InputField
            id="tableSearch"
            name="tableSearch"
            onChange={(e) => setSearchValue(e.currentTarget.value)}
            label={searchPlaceholder || t('common.search')}
            value={searchValue}
            icon={<SearchIcon />}
            className={styles.search}
            disabledBrowserSuggestions
          />
        </div>
      ) : null}

      <Table
        {...tableProps}
        dispatch={dispatch}
        childComponents={{
          cellText: {
            content: ({ rowData, field, column }) => getCell(rowData, field, column.bold),
            elementAttributes: ({ column }) => ({
              'data-column': column.title,
            }),
          },
          sortIcon: {
            content: ({ column }) => getSortIcon(column),
          },
        }}
      />
    </div>
  );
}

GenericTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    title: PropTypes.string,
  })),
  rows: PropTypes.arrayOf(PropTypes.shape({})),
  isSearchable: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
};

GenericTable.defaultProps = {
  columns: [],
  rows: [],
  isSearchable: false,
  searchPlaceholder: null,
};

export default GenericTable;
